import { Boxes, Building2, Check, ChevronDown, Database, FolderKanban, LayoutGrid, Plus, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  navigateToArchitectureProject,
  navigateToOrganization,
  navigateToProject,
  navigateToWorkspaceProject,
} from '../../App';
import { useArchitectureProjectsStore } from '../../store/useArchitectureProjectsStore';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { useProjectsStore } from '../../store/useProjectsStore';
import { useSchemaStore } from '../../store/useSchemaStore';
import { useWorkspaceStore, type DiagramKind } from '../../store/useWorkspaceStore';
import NameDialog from './NameDialog';

interface Props {
  kind: DiagramKind;
  diagramId: string;
  /** The editable diagram name, rendered as the last breadcrumb. */
  children: ReactNode;
}

interface MenuItem {
  key: string;
  label: string;
  icon: LucideIcon;
  active?: boolean;
  onSelect: () => void;
}

/** Persist whatever is being edited before leaving it, because the next editor replaces the in-memory diagram. */
function flushPendingSave() {
  const schema = useSchemaStore.getState();
  if (schema.dirty) schema.save();
  const arch = useArchitectureStore.getState();
  if (arch.dirty) arch.save();
}

function go(action: () => void) {
  flushPendingSave();
  action();
}

function Menu({ label, icon: Icon, items, actions = [], footer, compact }: { label?: string; icon: LucideIcon; items: MenuItem[]; actions?: MenuItem[]; footer?: MenuItem; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const choose = (item: MenuItem) => {
    setOpen(false);
    item.onSelect();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-lg text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white ${
          compact ? 'p-1.5' : 'px-2 py-1.5'
        }`}
        title={compact ? 'Switch diagram' : undefined}
      >
        {!compact && <Icon className="h-3.5 w-3.5 text-gray-400" />}
        {label && <span className="max-w-[10rem] truncate">{label}</span>}
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 max-h-80 w-64 overflow-auto rounded-xl border border-gray-200/80 bg-panel p-1 shadow-pop dark:border-white/10 dark:bg-gray-900">
          {items.length === 0 && <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Nothing here yet</div>}
          {items.map((item) => (
            <button
              key={item.key}
              onClick={() => choose(item)}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
            >
              <item.icon className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="flex-1 truncate">{item.label}</span>
              {item.active && <Check className="h-4 w-4 shrink-0 text-sky-500" />}
            </button>
          ))}
          {(actions.length > 0 || footer) && <div className="my-1 h-px bg-gray-200 dark:bg-white/10" />}
          {actions.map((action) => (
            <button
              key={action.key}
              onClick={() => choose(action)}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-sky-600 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-500/10"
            >
              <action.icon className="h-4 w-4 shrink-0" />
              {action.label}
            </button>
          ))}
          {footer && (
            <>
              <button
                onClick={() => choose(footer)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
              >
                <footer.icon className="h-4 w-4 shrink-0" />
                {footer.label}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ContextSwitcher({ kind, diagramId, children }: Props) {
  const organizations = useWorkspaceStore((s) => s.organizations);
  const workspaceProjects = useWorkspaceStore((s) => s.projects);
  const erDiagrams = useProjectsStore((s) => s.projects);
  const archDiagrams = useArchitectureProjectsStore((s) => s.projects);

  const can = useWorkspaceStore((s) => s.can);
  const createOrganization = useWorkspaceStore((s) => s.createOrganization);
  const createProject = useWorkspaceStore((s) => s.createProject);
  const createDiagram = useWorkspaceStore((s) => s.createDiagram);
  const [creating, setCreating] = useState<'org' | 'project' | DiagramKind | null>(null);
  const [seedSample, setSeedSample] = useState(false);

  const project = workspaceProjects.find((p) => (kind === 'er' ? p.erIds : p.archIds).includes(diagramId));
  const org = organizations.find((o) => o.id === project?.orgId);

  const openDiagram = (k: DiagramKind, id: string) => (k === 'er' ? navigateToProject(id) : navigateToArchitectureProject(id));

  /** First diagram of a project (ER first), or the project overview when it has none. */
  const openProject = (orgId: string, projectId: string) => {
    const p = workspaceProjects.find((x) => x.id === projectId);
    if (p?.erIds[0]) navigateToProject(p.erIds[0]);
    else if (p?.archIds[0]) navigateToArchitectureProject(p.archIds[0]);
    else navigateToWorkspaceProject(orgId, projectId);
  };

  const openOrg = (orgId: string) => {
    const target = workspaceProjects.find((p) => p.orgId === orgId && (p.erIds.length || p.archIds.length));
    if (target) openProject(orgId, target.id);
    else navigateToOrganization(orgId);
  };

  if (!project || !org) return <>{children}</>;

  const orgItems: MenuItem[] = organizations.map((o) => ({
    key: o.id,
    label: o.name,
    icon: Building2,
    active: o.id === org.id,
    onSelect: () => go(() => (o.id === org.id ? navigateToOrganization(o.id) : openOrg(o.id))),
  }));

  const projectItems: MenuItem[] = workspaceProjects
    .filter((p) => p.orgId === org.id)
    .map((p) => ({
      key: p.id,
      label: p.name,
      icon: FolderKanban,
      active: p.id === project.id,
      onSelect: () => go(() => (p.id === project.id ? navigateToWorkspaceProject(org.id, p.id) : openProject(org.id, p.id))),
    }));

  const diagramItems: MenuItem[] = [
    ...erDiagrams
      .filter((d) => project.erIds.includes(d.id))
      .map((d) => ({
        key: `er:${d.id}`,
        label: d.name,
        icon: Database,
        active: kind === 'er' && d.id === diagramId,
        onSelect: () => go(() => openDiagram('er', d.id)),
      })),
    ...archDiagrams
      .filter((d) => project.archIds.includes(d.id))
      .map((d) => ({
        key: `arch:${d.id}`,
        label: d.name,
        icon: Boxes,
        active: kind === 'architecture' && d.id === diagramId,
        onSelect: () => go(() => openDiagram('architecture', d.id)),
      })),
  ];

  const addAction = (key: string, label: string, what: 'org' | 'project' | DiagramKind): MenuItem => ({
    key,
    label,
    icon: Plus,
    onSelect: () => setCreating(what),
  });

  const dialogs: Record<string, { title: string; description: string; placeholder: string }> = {
    org: { title: 'New organization', description: 'A workspace for your team\'s projects.', placeholder: 'e.g. Acme Inc.' },
    project: { title: 'New project', description: `Created inside ${org.name}.`, placeholder: 'e.g. Payments Platform' },
    er: { title: 'New ER diagram', description: `Added to ${project.name}.`, placeholder: 'e.g. Core Schema' },
    architecture: { title: 'New architecture diagram', description: `Added to ${project.name}.`, placeholder: 'e.g. Production Infrastructure' },
  };

  const handleCreate = async (name: string) => {
    const what = creating;
    if (!what) return;
    flushPendingSave();
    if (what === 'org') {
      const created = await createOrganization(name);
      setCreating(null);
      navigateToOrganization(created.id);
    } else if (what === 'project') {
      const created = await createProject(org.id, name);
      setCreating(null);
      navigateToWorkspaceProject(org.id, created.id);
    } else {
      const id = await createDiagram(project.id, what, name, what === 'er' && seedSample);
      setCreating(null);
      openDiagram(what, id);
    }
  };

  const sep = <span className="text-gray-300 dark:text-gray-600">/</span>;

  return (
    <div className="flex min-w-0 items-center gap-0.5">
      <Menu
        label={org.name}
        icon={Building2}
        items={orgItems}
        actions={[addAction('new-org', 'New organization', 'org')]}
        footer={{ key: 'all-orgs', label: 'All organizations', icon: LayoutGrid, onSelect: () => go(() => (window.location.hash = '#/')) }}
      />
      {sep}
      <Menu
        label={project.name}
        icon={FolderKanban}
        items={projectItems}
        actions={can(org.id, 'projects.manage') ? [addAction('new-project', 'New project', 'project')] : []}
        footer={{ key: 'org-home', label: 'Organization overview', icon: LayoutGrid, onSelect: () => go(() => navigateToOrganization(org.id)) }}
      />
      {sep}
      {children}
      <Menu
        icon={Database}
        compact
        items={diagramItems}
        actions={can(org.id, 'diagrams.create') ? [addAction('new-er', 'New ER diagram', 'er'), addAction('new-arch', 'New architecture diagram', 'architecture')] : []}
        footer={{ key: 'project-home', label: 'Project overview', icon: LayoutGrid, onSelect: () => go(() => navigateToWorkspaceProject(org.id, project.id)) }}
      />
      {creating && (
        <NameDialog
          title={dialogs[creating].title}
          description={dialogs[creating].description}
          placeholder={dialogs[creating].placeholder}
          onCancel={() => setCreating(null)}
          onConfirm={handleCreate}
        >
          {creating === 'er' && (
            <label className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={seedSample} onChange={(e) => setSeedSample(e.target.checked)} />
              Start from example schema
            </label>
          )}
        </NameDialog>
      )}
    </div>
  );
}
